<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  CATEGORY_KEYS,
  NOTIFICATION_CATEGORIES,
  typesForCategories,
} from 'dashboard/helper/notificationCategories';

import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['filter']);
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const checkboxClass =
  "m-0 border-[1.5px] shadow border-n-weak appearance-none rounded-[var(--workspace-radius,4px)] w-4 h-4 dark:bg-n-background focus:ring-1 focus:ring-n-weak dark:focus:ring-n-strong checked:bg-n-brand dark:checked:bg-n-brand after:content-[''] after:text-white checked:after:content-['✓'] after:flex after:items-center after:justify-center checked:border-t checked:border-n-blue-10 checked:border-b-0 checked:border-r-0 checked:border-l-0 after:text-center after:text-xs after:font-bold after:relative after:-top-[1.5px]";

const showSortMenu = ref(false);
const displayOptions = ref([
  {
    name: t('INBOX.DISPLAY_MENU.DISPLAY_OPTIONS.SNOOZED'),
    key: wootConstants.INBOX_DISPLAY_BY.SNOOZED,
    selected: false,
    type: wootConstants.INBOX_FILTER_TYPE.STATUS,
  },
  {
    name: t('INBOX.DISPLAY_MENU.DISPLAY_OPTIONS.READ'),
    key: wootConstants.INBOX_DISPLAY_BY.READ,
    selected: false,
    type: wootConstants.INBOX_FILTER_TYPE.TYPE,
  },
]);
const sortOptions = [
  {
    name: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.NEWEST'),
    key: wootConstants.INBOX_SORT_BY.NEWEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.OLDEST'),
    key: wootConstants.INBOX_SORT_BY.OLDEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
];
const activeSort = ref(wootConstants.INBOX_SORT_BY.NEWEST);
const activeDisplayFilter = ref({
  status: '',
  type: '',
});
const selectedCategories = ref([...CATEGORY_KEYS]);
const selectedNotificationTypes = ref([]);

const activeSortOption = computed(() => {
  return (
    sortOptions.find(option => option.key === activeSort.value)?.name || ''
  );
});

const showTypeFilters = computed(() => selectedCategories.value.length === 1);

const typeFilterOptions = computed(() => {
  if (!showTypeFilters.value) return [];
  return typesForCategories(selectedCategories.value);
});

const saveSelectedDisplayFilter = () => {
  updateUISettings({
    inbox_filter_by: {
      ...activeDisplayFilter.value,
      sort_by: activeSort.value || wootConstants.INBOX_SORT_BY.NEWEST,
      categories: [...selectedCategories.value],
      notification_types: [...selectedNotificationTypes.value],
    },
  });
};

const updateDisplayOption = option => {
  displayOptions.value.forEach(displayOption => {
    if (displayOption.key === option.key) {
      displayOption.selected = !option.selected;
      activeDisplayFilter.value[displayOption.type] = displayOption.selected
        ? displayOption.key
        : '';
      saveSelectedDisplayFilter();
      emit('filter', option);
    }
  });
};

const openSortMenu = () => {
  showSortMenu.value = !showSortMenu.value;
};

const onSortOptionClick = option => {
  activeSort.value = option.key;
  showSortMenu.value = false;
  saveSelectedDisplayFilter();
  emit('filter', option);
};

const toggleCategory = categoryKey => {
  const isSelected = selectedCategories.value.includes(categoryKey);
  selectedCategories.value = isSelected
    ? selectedCategories.value.filter(key => key !== categoryKey)
    : [...selectedCategories.value, categoryKey];

  if (selectedCategories.value.length === 1) {
    selectedNotificationTypes.value = typesForCategories(
      selectedCategories.value
    );
  } else {
    selectedNotificationTypes.value = [];
  }

  saveSelectedDisplayFilter();
  emit('filter', {
    type: wootConstants.INBOX_FILTER_TYPE.CATEGORIES,
    selectedKeys: [...selectedCategories.value],
    selectedTypes: [...selectedNotificationTypes.value],
    keepOpen: true,
  });
};

const toggleNotificationType = notificationType => {
  const isSelected = selectedNotificationTypes.value.includes(notificationType);
  selectedNotificationTypes.value = isSelected
    ? selectedNotificationTypes.value.filter(type => type !== notificationType)
    : [...selectedNotificationTypes.value, notificationType];

  saveSelectedDisplayFilter();
  emit('filter', {
    type: wootConstants.INBOX_FILTER_TYPE.NOTIFICATION_TYPES,
    selectedKeys: [...selectedNotificationTypes.value],
    keepOpen: true,
  });
};

const typeLabel = notificationType => {
  return t(`INBOX.TYPES_NEXT.${notificationType.toUpperCase()}`);
};

const setSavedFilter = () => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const {
    status,
    type,
    sort_by: sortBy,
    categories,
    notification_types: notificationTypes,
  } = filterBy;
  activeSort.value = sortBy || wootConstants.INBOX_SORT_BY.NEWEST;
  displayOptions.value.forEach(option => {
    option.selected =
      option.type === wootConstants.INBOX_FILTER_TYPE.STATUS
        ? option.key === status
        : option.key === type;
    activeDisplayFilter.value[option.type] = option.selected ? option.key : '';
  });
  selectedCategories.value = Array.isArray(categories)
    ? categories.filter(key => CATEGORY_KEYS.includes(key))
    : [...CATEGORY_KEYS];
  if (selectedCategories.value.length === 1) {
    if (Array.isArray(notificationTypes)) {
      const allowed = new Set(typesForCategories(selectedCategories.value));
      selectedNotificationTypes.value = notificationTypes.filter(item =>
        allowed.has(item)
      );
    } else {
      selectedNotificationTypes.value = typesForCategories(
        selectedCategories.value
      );
    }
  } else {
    selectedNotificationTypes.value = [];
  }
};

onMounted(() => {
  setSavedFilter();
});
</script>

<template>
  <div
    class="flex flex-col bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container shadow-lg z-50 max-w-72 min-w-[220px] w-72 max-h-[min(70vh,32rem)] overflow-y-auto rounded-2xl divide-y divide-n-weak dark:divide-n-strong"
  >
    <div class="flex items-center gap-2 justify-between p-3 rounded-t-lg h-11">
      <div class="flex gap-1.5 min-w-0">
        <span class="i-ri-swap-line size-3.5 text-n-slate-12" />
        <span class="text-xs font-medium text-n-slate-12 truncate min-w-0">
          {{ $t('INBOX.DISPLAY_MENU.SORT') }}
        </span>
      </div>
      <div v-on-clickaway="() => (showSortMenu = false)" class="relative">
        <NextButton
          :label="activeSortOption"
          icon="i-ri-arrow-down-s-line"
          slate
          trailing-icon
          xs
          outline
          class="w-fit min-w-20 max-w-32"
          @click="openSortMenu"
        />
        <div
          v-if="showSortMenu"
          class="absolute flex flex-col gap-0.5 bg-n-alpha-3 backdrop-blur-[100px] z-60 rounded-xl p-0.5 w-fit min-w-20 max-w-32 top-px outline outline-1 outline-n-container dark:outline-n-strong"
        >
          <div
            v-for="option in sortOptions"
            :key="option.key"
            role="button"
            class="flex rounded-md h-5 w-full items-center justify-between px-1.5 py-0.5 gap-2 whitespace-nowrap"
            :class="{
              'bg-n-brand/10 dark:bg-n-brand/10': activeSort === option.key,
            }"
            @click.stop="onSortOptionClick(option)"
          >
            <span
              class="text-xs font-medium hover:text-n-brand truncate min-w-0 dark:hover:text-n-brand"
              :class="{
                'text-n-blue-11 dark:text-n-blue-11': activeSort === option.key,
                'text-n-slate-11': activeSort !== option.key,
              }"
            >
              {{ option.name }}
            </span>
            <span
              v-if="activeSort === option.key"
              class="i-ri-check-line size-2.5 flex-shrink-0 text-n-blue-11"
            />
          </div>
        </div>
      </div>
    </div>
    <div>
      <span class="px-3 py-4 text-xs font-medium text-n-slate-11">
        {{ $t('INBOX.DISPLAY_MENU.DISPLAY') }}
      </span>
      <div class="flex flex-col divide-y divide-n-weak dark:divide-n-strong">
        <div
          v-for="option in displayOptions"
          :key="option.key"
          class="flex items-center px-3 py-2 gap-1.5 h-9"
        >
          <input
            :id="option.key"
            type="checkbox"
            :name="option.key"
            :checked="option.selected"
            :class="checkboxClass"
            @change="updateDisplayOption(option)"
          />
          <label
            :for="option.key"
            class="text-xs font-medium text-n-slate-12 !ml-0 !mr-0 dark:text-n-slate-12"
          >
            {{ option.name }}
          </label>
        </div>
      </div>
    </div>
    <div>
      <span class="px-3 py-4 text-xs font-medium text-n-slate-11">
        {{ $t('INBOX.DISPLAY_MENU.CATEGORIES') }}
      </span>
      <div class="flex flex-col divide-y divide-n-weak dark:divide-n-strong">
        <div
          v-for="category in NOTIFICATION_CATEGORIES"
          :key="category.key"
          class="flex items-center px-3 py-2 gap-1.5 min-h-9"
        >
          <input
            :id="`inbox-category-${category.key}`"
            type="checkbox"
            :name="category.key"
            :checked="selectedCategories.includes(category.key)"
            :class="checkboxClass"
            @change="toggleCategory(category.key)"
          />
          <label
            :for="`inbox-category-${category.key}`"
            class="flex items-center gap-1.5 text-xs font-medium text-n-slate-12 !ml-0 !mr-0 dark:text-n-slate-12"
          >
            <span class="size-3.5 text-n-slate-11" :class="[category.icon]" />
            {{ $t(`INBOX.CATEGORIES.${category.key.toUpperCase()}`) }}
          </label>
        </div>
      </div>
    </div>
    <div v-if="showTypeFilters">
      <span class="px-3 py-4 text-xs font-medium text-n-slate-11">
        {{ $t('INBOX.DISPLAY_MENU.TYPES') }}
      </span>
      <div class="flex flex-col divide-y divide-n-weak dark:divide-n-strong">
        <div
          v-for="notificationType in typeFilterOptions"
          :key="notificationType"
          class="flex items-center px-3 py-2 gap-1.5 min-h-9"
        >
          <input
            :id="`inbox-type-${notificationType}`"
            type="checkbox"
            :name="notificationType"
            :checked="selectedNotificationTypes.includes(notificationType)"
            :class="checkboxClass"
            @change="toggleNotificationType(notificationType)"
          />
          <label
            :for="`inbox-type-${notificationType}`"
            class="text-xs font-medium text-n-slate-12 !ml-0 !mr-0 dark:text-n-slate-12"
          >
            {{ typeLabel(notificationType) }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
